export const AGE_KEYS = ["18-22", "23-29", "30-39", "40-49", "50-59", "60-69", "70-79", "80-89", ">89"];
export const GENDER_KEYS = ["Male", "Female"];
export const IMPORTANCE_VALUES = ["slight", "moderate", "strong"];
export const FIT_VALUES = ["mismatch", ...IMPORTANCE_VALUES];
export const CRITERIA = {age: AGE_KEYS, gender: GENDER_KEYS};

export const supportsDemographicSelection = (pipelineType, fileType) =>
    ["FILE_EXCHANGE", "FOLDER_EXCHANGE"].includes(pipelineType) && fileType === "IMPRESSIONS";

const emptyCriterion = (keys) => ({
    enabled: false, importance: "", fits: Object.fromEntries(keys.map(key => [key, ""]))
});

export function demographicProfileToForm(profile, pipelineType, fileType) {
    const form = {
        enabled: false, clearRequested: false, hasSavedProfile: profile != null, loadError: "",
        age: emptyCriterion(AGE_KEYS), gender: emptyCriterion(GENDER_KEYS)
    };
    if (profile == null) return form;
    const invalid = () => ({...form, loadError: "The saved demographic profile is unsupported or invalid. Resolve it before saving this workflow."});
    if (!supportsDemographicSelection(pipelineType, fileType) || !profile || typeof profile !== "object" ||
        profile.version !== 1 || Object.keys(profile).some(key => !["version", "age", "gender"].includes(key))) return invalid();
    form.enabled = true;
    for (const [name, keys] of Object.entries(CRITERIA)) {
        if (!Object.prototype.hasOwnProperty.call(profile, name)) continue;
        const criterion = profile[name];
        if (!criterion || Object.keys(criterion).some(key => !["importance", "fits"].includes(key)) ||
            !criterion.fits || Object.keys(criterion.fits).length !== keys.length ||
            keys.some(key => !Object.prototype.hasOwnProperty.call(criterion.fits, key))) return invalid();
        form[name] = {enabled: true, importance: criterion.importance, fits: {...criterion.fits}};
    }
    return Object.keys(validateDemographicForm(form, true)).length ? invalid() : form;
}

export function validateDemographicForm(form, supported) {
    if (form.loadError) return {profile: form.loadError};
    if (!supported) {
        return form.hasSavedProfile && !form.clearRequested
            ? {profile: "Demographic selection requires IMPRESSIONS with File Exchange or Folder Exchange. Return to that workflow and explicitly disable it before changing type."} : {};
    }
    if (!form.enabled) return {};
    const errors = {};
    if (!Object.keys(CRITERIA).some(name => form[name].enabled)) errors.profile = "Select at least one criterion: Age or Gender.";
    for (const [name, keys] of Object.entries(CRITERIA)) {
        const criterion = form[name];
        if (!criterion.enabled) continue;
        if (!IMPORTANCE_VALUES.includes(criterion.importance)) errors[`${name}.importance`] = "Select an importance.";
        if (Object.keys(criterion.fits).length !== keys.length || Object.keys(criterion.fits).some(key => !keys.includes(key))) {
            errors.profile = "Fit maps must contain exactly the supported values.";
        }
        keys.forEach(key => {
            if (!FIT_VALUES.includes(criterion.fits[key])) errors[`${name}.${key}`] = "Select a fit.";
        });
    }
    return errors;
}

// Return a payload fragment: {} preserves omission, while an explicit disable on Edit sends null.
export function demographicProfilePayload(form, pipelineType, fileType, editing = false) {
    const supported = supportsDemographicSelection(pipelineType, fileType);
    const errors = validateDemographicForm(form, supported);
    if (Object.keys(errors).length) throw new Error(Object.values(errors)[0]);
    if (editing && form.clearRequested && !form.enabled) return {demographic_selection_profile: null};
    if (!supported || !form.enabled) return {};
    const profile = {version: 1};
    for (const [name, keys] of Object.entries(CRITERIA)) {
        if (form[name].enabled) profile[name] = {
            importance: form[name].importance,
            fits: Object.fromEntries(keys.map(key => [key, form[name].fits[key]]))
        };
    }
    return {demographic_selection_profile: profile};
}
